import * as THREE from 'three';
import { TractorBeam } from '../effects/TrailEffect';
import { TransactionShip } from './TransactionShip';
import { MempoolNebula } from '../effects/MempoolNebula';

export interface MiningCollectorConfig {
  poolName: string;
  hashrate: number; // Hash rate of this mining pool
  position: THREE.Vector3;
  color: THREE.Color;
  beamCount: number; // Number of simultaneous tractor beams
  targetingRange: number; // Maximum range for targeting ships
  extractionSpeed: number; // Speed of ship extraction
  feePreference: 'high' | 'medium' | 'low' | 'any'; // Fee rate preference
}

export interface MiningTarget {
  shipId: string;
  ship: TransactionShip;
  feeRate: number;
  distanceFromCollector: number;
  beamIndex: number;
  captureProgress: number; // 0-1, how much of the ship has been captured
  extractionStartTime: number;
}

/**
 * Phase 3: Mining Collector - represents a mining pool's collection operations
 * Extends tractor beams into mempool nebula to select and extract transaction ships
 */
export class MiningCollector {
  public readonly id: string;
  public readonly config: MiningCollectorConfig;
  private mesh: THREE.Group;
  private tractorBeams: TractorBeam[];
  private activeTargets: Map<string, MiningTarget>;
  private lastTargetingUpdate: number = 0;
  private targetingInterval: number = 1000; // Update targeting every 1 second
  private collectorPosition: THREE.Vector3;
  private animationTime: number = 0;

  // Visual components
  private collectorMesh: THREE.Mesh;
  private engineGlows: THREE.Mesh[];
  private antennaArray: THREE.Group;

  // Performance metrics
  public miningPower: number;
  public competitiveness: number; // How aggressively this collector competes for transactions

  constructor(config: MiningCollectorConfig) {
    console.log(`⛏️ Creating MiningCollector for ${config.poolName} at position:`, config.position);
    this.id = `collector-${config.poolName}`;
    this.config = config;
    this.collectorPosition = config.position.clone();
    this.activeTargets = new Map();
    this.tractorBeams = [];
    
    // Initialize mining metrics based on hashrate
    this.miningPower = Math.min(config.hashrate / 100000000000000, 2.0); // Normalize to 0-2 range
    this.competitiveness = Math.min(config.hashrate / 50000000000000, 1.5); // Higher hashrate = more competitive

    console.log(`🔧 Creating collector mesh for ${config.poolName}...`);
    this.createCollectorMesh();
    console.log(`🔗 Initializing ${config.beamCount} tractor beams...`);
    this.initializeTractorBeams();
    console.log(`✅ MiningCollector ${config.poolName} created with ${this.tractorBeams.length} beams`);
  }

  private createCollectorMesh(): void {
    this.mesh = new THREE.Group();
    
    // Main collector body - large angular mining platform (much bigger!)
    const collectorGeometry = new THREE.ConeGeometry(8, 12, 8); // Increased from 3,6,6 to 8,12,8
    const collectorMaterial = new THREE.MeshStandardMaterial({
      color: this.config.color,
      metalness: 0.8,
      roughness: 0.2,
      emissive: this.config.color,
      emissiveIntensity: 0.5 // Increased emissive intensity for better visibility
    });
    
    this.collectorMesh = new THREE.Mesh(collectorGeometry, collectorMaterial);
    this.collectorMesh.rotation.x = Math.PI; // Point downward toward mempool
    
    // Set userData on the mesh (not the group) for raycasting detection
    this.collectorMesh.userData = {
      id: `collector-${this.config.poolName}`,
      type: 'mining-collector',
      interactive: true,
      collectorData: {
        poolName: this.config.poolName,
        hashrate: this.config.hashrate,
        miningPower: this.miningPower,
        competitiveness: this.competitiveness,
        beamCount: this.config.beamCount,
        targetingRange: this.config.targetingRange,
        extractionSpeed: this.config.extractionSpeed,
        feePreference: this.config.feePreference,
        position: this.collectorPosition
      }
    };
    
    this.mesh.add(this.collectorMesh);

    // Engine glows for movement (larger and more visible)
    this.engineGlows = [];
    for (let i = 0; i < 4; i++) {
      const glowGeometry = new THREE.SphereGeometry(2, 8, 8); // Increased from 0.5 to 2
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: this.config.color,
        transparent: true,
        opacity: 0.8 // Increased opacity
      });
      
      const engineGlow = new THREE.Mesh(glowGeometry, glowMaterial);
      const angle = (i / 4) * Math.PI * 2;
      engineGlow.position.set(
        Math.cos(angle) * 6, // Increased from 2 to 6
        8, // Increased from 4 to 8
        Math.sin(angle) * 6
      );
      
      this.engineGlows.push(engineGlow);
      this.mesh.add(engineGlow);
    }

    // Antenna array for mining signal reception (larger)
    this.antennaArray = new THREE.Group();
    for (let i = 0; i < 6; i++) {
      const antennaGeometry = new THREE.CylinderGeometry(0.3, 0.3, 8); // Increased from 0.1,0.1,4 to 0.3,0.3,8
      const antennaMaterial = new THREE.MeshStandardMaterial({
        color: 0xaaaaaa, // Lighter color for better visibility
        metalness: 0.9,
        roughness: 0.1,
        emissive: 0x333333,
        emissiveIntensity: 0.2
      });
      
      const antenna = new THREE.Mesh(antennaGeometry, antennaMaterial);
      const angle = (i / 6) * Math.PI * 2;
      antenna.position.set(
        Math.cos(angle) * 4, // Increased from 1.5 to 4
        4, // Increased from 2 to 4
        Math.sin(angle) * 4
      );
      antenna.rotation.z = Math.PI / 6; // Slight outward angle
      
      this.antennaArray.add(antenna);
    }
    this.mesh.add(this.antennaArray);

    // Set the position of the entire group
    this.mesh.position.copy(this.collectorPosition);
    
    console.log(`🎨 Created interactive collector mesh for ${this.config.poolName} at position:`, this.collectorPosition);
  }

  private initializeTractorBeams(): void {
    for (let i = 0; i < this.config.beamCount; i++) {
      const beam = new TractorBeam();
      this.tractorBeams.push(beam);
      this.mesh.add(beam.getMesh());
    }
  }

  /**
   * Phase 3: Scan mempool nebula for potential targets based on fee priority
   */
  public scanForTargets(
    _mempoolNebula: MempoolNebula, // Underscore prefix for intentionally unused
    availableShips: Map<string, TransactionShip>
  ): void {
    const now = Date.now();
    if (now - this.lastTargetingUpdate < this.targetingInterval) return;
    
    this.lastTargetingUpdate = now;
    
    // mempoolNebula is used for context but targeting is based on ship trajectory phases
    // and distance calculations - future enhancement could use nebula density
    
    // Get all ships orbiting in mempool
    const candidateShips = Array.from(availableShips.entries())
      .filter(([, ship]) => ship.trajectory.phase === 'orbiting-mempool')
      .map(([id, ship]) => ({
        id,
        ship,
        feeRate: ship.data.feeRate,
        distance: this.collectorPosition.distanceTo(ship.trajectory.currentPosition)
      }))
      .filter(candidate => candidate.distance <= this.config.targetingRange);

    // Sort by fee priority and distance (fee-based targeting algorithm)
    candidateShips.sort((a, b) => {
      // Primary sort: fee rate (higher is better)
      const feeScore = b.feeRate - a.feeRate;
      if (Math.abs(feeScore) > 5) return feeScore; // Significant fee difference
      
      // Secondary sort: distance (closer is better) 
      return a.distance - b.distance;
    });

    // Select targets based on available beam capacity
    const availableBeams = this.getAvailableBeamIndices();
    const newTargets = candidateShips
      .slice(0, availableBeams.length)
      .filter(candidate => this.shouldTargetShip(candidate.feeRate));

    // Assign new targets to beams
    newTargets.forEach((target, index) => {
      if (index < availableBeams.length) {
        this.assignTarget(target.id, target.ship, target.feeRate, availableBeams[index]);
      }
    });
  }

  private shouldTargetShip(feeRate: number): boolean {
    // Mining competition: check fee preferences and competitiveness
    switch (this.config.feePreference) {
      case 'high':
        return feeRate >= 50 * this.competitiveness;
      case 'medium':
        return feeRate >= 20 * this.competitiveness && feeRate < 100;
      case 'low':
        return feeRate >= 5 * this.competitiveness && feeRate < 30;
      case 'any':
        return feeRate >= 1 * this.competitiveness;
      default:
        return feeRate >= 10;
    }
  }

  private getAvailableBeamIndices(): number[] {
    const available: number[] = [];
    for (let i = 0; i < this.config.beamCount; i++) {
      const isBeamFree = !Array.from(this.activeTargets.values())
        .some(target => target.beamIndex === i);
      if (isBeamFree) {
        available.push(i);
      }
    }
    return available;
  }

  private assignTarget(
    shipId: string, 
    ship: TransactionShip, 
    feeRate: number, 
    beamIndex: number
  ): void {
    // Check if another collector is already targeting this ship
    if (ship.trajectory.phase === 'selected') return;

    const target: MiningTarget = {
      shipId,
      ship,
      feeRate,
      distanceFromCollector: this.collectorPosition.distanceTo(ship.trajectory.currentPosition),
      beamIndex,
      captureProgress: 0,
      extractionStartTime: Date.now()
    };

    this.activeTargets.set(shipId, target);
    
    // Activate tractor beam
    const beam = this.tractorBeams[beamIndex];
    beam.activate(
      this.getBeamStartPosition(beamIndex),
      ship.trajectory.currentPosition,
      this.miningPower
    );

    // Update ship phase to selected
    ship.trajectory.phase = 'selected';
  }

  private getBeamStartPosition(beamIndex: number): THREE.Vector3 {
    // Beams emanate from the bottom of the collector
    const angle = (beamIndex / this.config.beamCount) * Math.PI * 2;
    const beamOffset = new THREE.Vector3(
      Math.cos(angle) * 1.5,
      -2, // Bottom of collector
      Math.sin(angle) * 1.5
    );
    
    return this.collectorPosition.clone().add(beamOffset);
  }

  /**
   * Phase 3: Update tractor beam targeting and ship extraction
   */
  public update(deltaTime: number): void {
    this.animationTime += deltaTime;
    
    // Update collector animations
    this.updateCollectorAnimations(deltaTime);
    
    // Update active targeting and extraction
    this.updateTractorBeams(deltaTime);
    
    // Process ship extraction
    this.updateShipExtraction(deltaTime);
  }

  private updateCollectorAnimations(deltaTime: number): void {
    // Rotate antenna array
    this.antennaArray.rotation.y += deltaTime * 0.5;
    
    // Pulsing engine glows
    const pulseIntensity = 0.3 + 0.2 * Math.sin(this.animationTime * 2);
    this.engineGlows.forEach((glow, index) => {
      const phaseOffset = (index / this.engineGlows.length) * Math.PI * 2;
      const glowIntensity = pulseIntensity + 0.1 * Math.sin(this.animationTime * 3 + phaseOffset);
      
      // Safe opacity access for MeshBasicMaterial
      if (glow.material instanceof THREE.MeshBasicMaterial) {
        glow.material.opacity = glowIntensity;
      }
    });

    // Collector body subtle movement
    const hoverOffset = Math.sin(this.animationTime * 1.5) * 0.5;
    this.collectorMesh.position.y = this.collectorPosition.y + hoverOffset;
  }

  private updateTractorBeams(deltaTime: number): void {
    for (const [shipId, target] of this.activeTargets) {
      const beam = this.tractorBeams[target.beamIndex];
      
      // Update beam positions to follow target ship
      beam.updatePositions(
        this.getBeamStartPosition(target.beamIndex),
        target.ship.trajectory.currentPosition
      );
      
      beam.update(deltaTime);
    }
  }

  private updateShipExtraction(deltaTime: number): void {
    const extractionsToComplete: string[] = [];
    
    for (const [shipId, target] of this.activeTargets) {
      // Increase capture progress based on mining power and extraction speed
      const captureRate = this.miningPower * this.config.extractionSpeed * deltaTime;
      target.captureProgress = Math.min(1.0, target.captureProgress + captureRate);
      
      // Apply extraction force to ship
      if (target.captureProgress > 0.3) {
        this.applyExtractionForce(target);
      }
      
      // Complete extraction when fully captured
      if (target.captureProgress >= 1.0) {
        extractionsToComplete.push(shipId);
      }
    }
    
    // Complete extractions
    extractionsToComplete.forEach(shipId => {
      this.completeExtraction(shipId);
    });
  }

  private applyExtractionForce(target: MiningTarget): void {
    // Pull ship toward collector with increasing force
    const ship = target.ship;
    const collectorPos = this.collectorPosition;
    const shipPos = ship.trajectory.currentPosition;
    
    const direction = new THREE.Vector3().subVectors(collectorPos, shipPos).normalize();
    const extractionForce = target.captureProgress * this.config.extractionSpeed * 0.1;
    
    // Move ship toward collector
    const newPosition = shipPos.clone().add(direction.multiplyScalar(extractionForce));
    ship.trajectory.currentPosition.copy(newPosition);
    ship.mesh.position.copy(newPosition);
  }

  private completeExtraction(shipId: string): void {
    const target = this.activeTargets.get(shipId);
    if (!target) return;
    
    // Deactivate tractor beam
    const beam = this.tractorBeams[target.beamIndex];
    beam.deactivate();
    
    // Remove target from active list
    this.activeTargets.delete(shipId);
    
    // Ship will be handled by mining assembly in Phase 4
    console.log(`Mining collector ${this.id} completed extraction of transaction ${shipId} (${target.feeRate} sat/vB)`);
  }

  /**
   * Get current mining status for debugging/UI
   */
  public getMiningStatus(): {
    activeTargets: number;
    totalMiningPower: number;
    averageTargetFeeRate: number;
    beamUtilization: number;
  } {
    const targets = Array.from(this.activeTargets.values());
    const avgFeeRate = targets.length > 0 
      ? targets.reduce((sum, target) => sum + target.feeRate, 0) / targets.length
      : 0;
    
    return {
      activeTargets: targets.length,
      totalMiningPower: this.miningPower,
      averageTargetFeeRate: avgFeeRate,
      beamUtilization: targets.length / this.config.beamCount
    };
  }

  /**
   * Get active targets for mining competition resolution
   */
  public getActiveTargets(): Map<string, MiningTarget> {
    return new Map(this.activeTargets);
  }

  public getMesh(): THREE.Group {
    return this.mesh;
  }

  public getPosition(): THREE.Vector3 {
    return this.collectorPosition.clone();
  }

  public releaseTarget(shipId: string): void {
    const target = this.activeTargets.get(shipId);
    if (target) {
      const beam = this.tractorBeams[target.beamIndex];
      beam.deactivate();
      this.activeTargets.delete(shipId);
      
      // Reset ship phase if still available
      if (target.ship && target.ship.trajectory.phase === 'selected') {
        target.ship.trajectory.phase = 'orbiting-mempool';
      }
    }
  }

  public dispose(): void {
    // Clean up tractor beams
    this.tractorBeams.forEach(beam => beam.dispose());
    
    // Clean up active targets
    this.activeTargets.clear();
    
    // Dispose of mesh materials and geometries
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });
  }
}